/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { observer } from 'mobx-react';
import * as React from 'react';
import WineClubInputRadioCheckbox from './wine-club-input-radio-checkbox';
import { getInputs } from './wine-club-input.data';
import { IWineClubResources } from './wine-club.props.autogenerated';
import { IWineClubInputProps, IWineClubStepComponentProps } from './wine-club.types';

//==============================================================================
/**
 * WineClubMembership component
 * @extends {React.Component<IWineClubStepComponentProps>}
 */
//==============================================================================
@observer
class WineClubMembership extends React.Component<IWineClubStepComponentProps> {

    //==========================================================================
    // Variables
    //==========================================================================

    private resources: IWineClubResources;

    //==========================================================================
    // Public Methods
    //==========================================================================

    public constructor(props: IWineClubStepComponentProps) {
        super(props);
        this.resources = this.props.resources || [];
        this._validateFields = this._validateFields.bind(this);
    }

    public render(): JSX.Element {
        const { resources } = this.props;

        return (
            <div className='wine-club-membership'>
                <div className='wine-club-content'>
                    <h2 className='wine-club-subheading'>{resources && resources.wineClub_membershipHeading}</h2>
                    <p className='wine-club-text'>{resources && resources.wineClub_membershipText}</p>
                    <div className='wine-club-form'>
                        <div className='wine-club-input-group form-group'>
                            {this._renderMembershipInputs()}
                        </div>
                        <div className='wine-club-button-group'>
                            {this.props.error &&
                                this.props.renderValidation(
                                    resources && resources.wineClub_membershipErrorMessage,
                                    'wine-club-error'
                                )
                            }
                            {resources && resources.wineClub_buttonNext &&
                                this.props.renderButton(
                                    resources.wineClub_buttonNext,
                                    'shipping',
                                    this.props.toggleStep,
                                    'wine-club-button-next btn-primary',
                                    this._validateFields,
                                    this.props.handleNext
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    //==========================================================================
    // Private Methods
    //==========================================================================

    // Renders form radio input from object data for the membership step.
    private _renderMembershipInputs(): JSX.Element[] {
        const membershipInput: IWineClubInputProps = getInputs(this.resources).find(input => input.id === 'membership') as IWineClubInputProps;
        const membershipOptions: string[] = membershipInput.options as string[];
        return membershipOptions.map((option, index) => {
            const isChecked = (option === this.props.fields.membership);
            return (
                <WineClubInputRadioCheckbox
                    key={index}
                    id={membershipInput.id}
                    label={membershipInput.label}
                    type={membershipInput.type}
                    value={option}
                    checked={isChecked}
                    // tslint:disable-next-line:jsx-no-lambda react-this-binding-issue
                    handleChange={event => {
                        this.props.handleChange(event);
                        this.props.resetAddressSelect();
                    }}
                />
            );
        });
    }

    // Validates membership radio input.
    private _validateFields(): boolean {
        return !!this.props.fields.membership;
    }
}

export default WineClubMembership;